import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import ListView from "../../component/ListView/ListView";
import { contentStats } from "../../component/ListView/contentCard";
import useMovieData from "./movidData";
import {
  deleteVideo,
  getLibraryByCategory,
  categoriesList,
} from "../../api/HttpApis";
import { channelListData } from "../../component/AddNewChannels/Data";
import { showToast } from "../../utils/Toastify/Toastify";
import { trimStr } from "../../utils/theme/ShortenedText/ShortenedText";
import { useSearch } from "../../context/SearchContext";
import FloatingButton from "../../component/FloatingButton/FloatingButton";
import RejectionModal from "../../component/ReviewFeedback/RejectionModal";
import { useRbac } from "../../rbac/RbacContext";
import useUrlTab from "../../hooks/useUrlTab";

// Default content type the unified Content page opens on.
const DEFAULT_CONTENT_TYPE = "";

// Series lives in its own navigation section, so it is excluded from the
// Content page's type filter.
const EXCLUDED_TYPES = ["Series"];

// Live TV channels are served by a separate endpoint (`/live-channel`) rather
// than `/library?content-type=`, so they are fetched differently below.
const isLiveChannelType = (name = "") =>
  name.toLowerCase() === "live channel" ||
  name.toLowerCase() === "live channels";

// Top tab bar filtering by QC/approval status (VideoMeta.qc_status).
const QC_STATUS_TABS = [
  { key: "all", label: "All" },
  { key: "Approved", label: "Approved" },
  { key: "Pending", label: "Pending" },
];

// Maps a content-type name to the route state `name` the Add Content form
// expects (see BasicInformation's nameMap) so "Add" pre-selects the type.
const ADD_STATE_BY_TYPE = {
  Movie: "movie",
  Podcast: "podcasts",
  "Short Clip": "short-clips",
  "Live Stream": "live-streams",
  "Live Channel": "Live Channels",
  "Live Event": "Live Events",
};

// The content-type dropdown options, kept for the session so coming back to
// this page can check ?type= straight away instead of waiting on the request
// again. Still refreshed on every visit.
let cachedContentTypeOptions = null;

const Movie = () => {
  const role = localStorage.getItem("role");
  const isCreator = role === "Creator";

  // Admin has no tabs to change this (see isCreator), so it must default to
  // Approved -- otherwise unreviewed Creator submissions would show up in the
  // main Content catalog before Admin has approved them via the Submissions
  // page. Creator defaults to "all" since they're checking the status of their
  // own submissions, pending included.
  const [qcStatus, setQcStatus] = useState(isCreator ? "all" : "Approved");

  const [movieData, setMovieData] = useState([]);
  const [deletingId, setDeletingId] = useState(null);
  // Kept in the URL (?type=) so a reload stays on the chosen content type.
  // Read unchecked here because the valid names come from the API; validated
  // just below once we know them.
  const [urlContentType, setSelectedContentType] = useUrlTab(
    null,
    DEFAULT_CONTENT_TYPE,
    "type"
  );
  const [contentTypeOptions, setContentTypeOptions] = useState(
    cachedContentTypeOptions || []
  );
  // `loaded` = the category list arrived, so unknown names can be rejected.
  // `settled` = the request finished either way. A failed request settles
  // without loading, so a valid ?type=Movie isn't thrown away as "unknown".
  // Both start true when the list is cached from an earlier visit.
  const [contentTypesLoaded, setContentTypesLoaded] = useState(
    !!cachedContentTypeOptions
  );
  const [contentTypesSettled, setContentTypesSettled] = useState(
    !!cachedContentTypeOptions
  );
  // A hand-edited / stale link (?type=Series, ?type=series, a deleted category)
  // falls back to "All" instead of fetching something the page is meant to
  // hide. Matching ignores case and resolves to the category's real name.
  const selectedContentType = (() => {
    if (!urlContentType) return DEFAULT_CONTENT_TYPE;
    const wanted = urlContentType.toLowerCase();
    if (EXCLUDED_TYPES.some((t) => t.toLowerCase() === wanted))
      return DEFAULT_CONTENT_TYPE;
    if (!contentTypesLoaded) return urlContentType;
    const match = contentTypeOptions.find(
      (o) => o.value.toLowerCase() === wanted
    );
    return match ? match.value : DEFAULT_CONTENT_TYPE;
  })();
  // Whether the list can be fetched yet. Only a specific ?type= has to wait
  // for the category list (so it's never fetched unchecked); "All" — the
  // usual way in — needs no check and loads straight away.
  const typeChecked =
    contentTypesSettled || selectedContentType === DEFAULT_CONTENT_TYPE;
  // Genre/rating/date only mean something for the type they were picked under,
  // so they're stored with that type and read as empty under any other. A type
  // change from anywhere — dropdown, sidebar "Content" link, hand-edited URL —
  // starts from clean filters without a separate reset step.
  const [typeFilters, setTypeFilters] = useState({
    forType: selectedContentType,
    date: "",
    rating: "",
    genre: "",
  });
  // Case-insensitive: a filter picked under ?type=movie before the category
  // list resolved it to "Movie" still applies.
  const isSameType = (a, b) => (a || "").toLowerCase() === (b || "").toLowerCase();
  const filtersApply = isSameType(typeFilters.forType, selectedContentType);
  const selectedDate = filtersApply ? typeFilters.date : "";
  const selectedRating = filtersApply ? typeFilters.rating : "";
  const selectedGenre = filtersApply ? typeFilters.genre : "";
  // Once the type moves on, forget the old type's filters — otherwise going
  // Movie(+Action) → Podcast → Movie would bring "Action" back. Done during
  // render (guarded, so it settles in one pass) so it also covers type changes
  // that don't come through the dropdown, like the sidebar link.
  if (
    !filtersApply &&
    (typeFilters.date || typeFilters.rating || typeFilters.genre)
  ) {
    setTypeFilters({
      forType: selectedContentType,
      date: "",
      rating: "",
      genre: "",
    });
  }
  // Id of the newest list request. A response whose id isn't the latest is
  // stale (the user changed filters/page while it was in flight) and is
  // dropped, so a slow earlier request can't overwrite newer rows.
  const latestRequest = useRef(0);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { getCard } = useMovieData();
  const { searchQuery } = useSearch();
  const { can } = useRbac();
  // Staff without content.create don't get the "Add Content" button (server also
  // enforces this — see requirePermission on POST /admin/add-content).
  const canCreateContent = can("content", "create");
  // The global search box, debounced. null (SearchContext's initial value) and
  // "" (what Searchbar sets on route change) both mean "no search". Starts
  // empty, not from searchQuery: arriving from another page, searchQuery still
  // holds that page's term until Searchbar clears it on the route change, and
  // starting from it would fetch filtered rows and then fetch again.
  const [search, setSearch] = useState("");
  useEffect(() => {
    const timer = setTimeout(() => setSearch(searchQuery || ""), 500);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Load the available content types for the filter dropdown, excluding Series.
  useEffect(() => {
    categoriesList()
      .then((res) => {
        const list = res?.data?.data || [];
        const options = list
          .filter((c) => !EXCLUDED_TYPES.includes(c.name))
          .map((c) => ({ label: c.name, value: c.name }));
        cachedContentTypeOptions = options;
        setContentTypeOptions(options);
        setContentTypesLoaded(true);
      })
      // Keep a cached list if there is one; otherwise there's nothing to show.
      .catch(() => {})
      .finally(() => setContentTypesSettled(true));
  }, []);

  // Drop an invalid ?type= from the URL (or fix its case) so it matches what's
  // shown. The only place an invalid type is rewritten.
  useEffect(() => {
    if (urlContentType !== selectedContentType)
      setSelectedContentType(selectedContentType);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlContentType, selectedContentType]);

  // Branches to the live-channel endpoint when needed and normalizes both
  // shapes into `{ items, total }`.
  const fetchLibrary = async (
    type,
    { date, rating, genre, page, perpage, search, qcStatus: qcStatusFilter }
  ) => {
    const contentType = type || DEFAULT_CONTENT_TYPE;

    if (isLiveChannelType(contentType)) {
      const res = await channelListData(
        date,
        "",
        rating,
        genre,
        page,
        perpage,
        search
      );
      return { items: res?.data || [], total: res?.totalCount || 0 };
    }

    const response = await getLibraryByCategory(
      contentType,
      {},
      date,
      "",
      rating,
      genre,
      perpage,
      page,
      search,
      qcStatusFilter && qcStatusFilter !== "all" ? qcStatusFilter : ""
    );
    const data = Array.isArray(response?.data?.data) ? response.data.data : [];
    const items = data.map((item) => ({
      id: item.id || item.original_id || item.video_id || item.movie_id,
      metadata_id: item.metadata_id || item.meta_id || item.meta_data_id,
      name: trimStr(item.name),
      media: item.portrait,
      status: item.status,
      qc_status: item.qc_status,
      qc_reason: item.qc_reason,
      qc_note: item.qc_note,
      rating: item.rating || "N/A",
      access: item.access,
      buttonText: "Edit",
      buttonText2: "Delete",
    }));
    return { items, total: response?.data?.total || 0 };
  };

  // The only list fetch. ListView calls it on first load, on page change, and
  // for page 1 whenever `queryKey` (below) changes — so filter changes, a new
  // search, or a ?type= change from a sidebar link / hand-edited URL just
  // update state and never fetch themselves.
  const fetchData = async (page, limit) => {
    // A specific ?type= waits for the category list so it's never fetched
    // unchecked; the list settling changes queryKey, which fetches.
    if (!typeChecked) {
      // Still counts as the newest request, so a reply already in flight for
      // the previous type is dropped instead of rendering under this one.
      ++latestRequest.current;
      setLoading(true);
      return;
    }
    const requestId = ++latestRequest.current;
    setLoading(true);
    try {
      const { items, total } = await fetchLibrary(selectedContentType, {
        date: selectedDate,
        rating: selectedRating,
        genre: selectedGenre,
        page,
        perpage: limit,
        search,
        qcStatus,
      });
      if (requestId !== latestRequest.current) return;
      setMovieData(items);
      setTotalCount(total);
    } catch (error) {
      if (requestId !== latestRequest.current) return;
      showToast(
        error.response?.data?.message || "Failed to fetch content",
        "error"
      );
    } finally {
      if (requestId === latestRequest.current) setLoading(false);
    }
  };

  // Opening a rejected item shows the reviewer's feedback first, so a creator
  // can see WHY it was rejected instead of only learning it from the email.
  // `null` = closed. Dismissing it continues to the editor.
  const [rejectionInfo, setRejectionInfo] = useState(null);

  const handleEdit = (id, metadata_id, item) => {
    const isRejected =
      String(item?.qc_status || "").trim().toLowerCase() === "rejected";
    if (isRejected) {
      setRejectionInfo({
        name: item?.name || "",
        reason: item?.qc_reason || null,
        note: item?.qc_note || null,
        // Carried so "Edit content" in the dialog continues to the editor.
        id,
        metadata_id,
      });
      return;
    }
    openEditor(id, metadata_id);
  };

  const openEditor = (id, metadata_id) => {
    const urlId = metadata_id || id;
    const hasMetadataId = Boolean(metadata_id);
    const url = hasMetadataId ? `/video/${urlId}` : `/video/${urlId}?meta=false`;

    navigate(url, {
      state: {
        movieId: id, // original video id → becomes resolvedMovieId
        video_id: id, // explicit video_id so resolvedMovieId is always set
        metadata_id: metadata_id || "",
        name: ADD_STATE_BY_TYPE[selectedContentType] || "movie",
      },
    });
  };

  const handleDelete = async (id, metadata_id) => {
    const confirmed = window.confirm(
      "Are you sure you want to delete this item?"
    );
    if (!confirmed) return;
    const deleteId = metadata_id || id;
    setDeletingId(deleteId);
    try {
      const response = await deleteVideo(deleteId);
      setMovieData((prev) =>
        prev.filter((item) => (item.metadata_id || item.id) !== deleteId)
      );
      // Keep the Total tile in step without a refetch.
      setTotalCount((c) => Math.max(0, (Number(c) || 0) - 1));
      showToast(response.data.message, "success");
    } catch (error) {
      showToast(error.response?.data?.message || "Failed to delete", "error");
    } finally {
      setDeletingId(null);
    }
  };

  const cardFor = getCard(handleEdit, handleDelete, deletingId);
  const stats = contentStats(movieData, totalCount);

  // Filter dropdowns only update state; ListView refetches page 1 because the
  // change shows up in `queryKey`.
  const handleFilterChange = (type, value) => {
    if (type === "contentType") {
      // Genre/rating/date clear themselves: they belong to the old type.
      setSelectedContentType(value || DEFAULT_CONTENT_TYPE);
      return;
    }
    if (type === "genre" || type === "rating" || type === "date") {
      setTypeFilters((prev) => ({
        ...(isSameType(prev.forType, selectedContentType)
          ? prev
          : { date: "", rating: "", genre: "" }),
        forType: selectedContentType,
        [type]: value,
      }));
    }
  };

  // Everything besides the page that decides which rows are listed. When it
  // changes, ListView goes back to page 1 and calls fetchData once.
  const queryKey = [
    typeChecked,
    selectedContentType,
    selectedDate,
    selectedRating,
    selectedGenre,
    search,
    qcStatus,
  ];

  return (
    <>
      <div>
        <ListView
          heading="Content"
          data={movieData}
          getCard={cardFor}
          stats={stats}
          onPageChange={fetchData}
          queryKey={queryKey}
          button={true}
          handleFilterChange={handleFilterChange}
          totalCount={totalCount}
          selectedDate={selectedDate}
          selectedRating={selectedRating}
          selectedGenre={selectedGenre}
          contentTypeOptions={contentTypeOptions}
          selectedContentType={selectedContentType}
          tabs={isCreator ? QC_STATUS_TABS : []}
          activeTab={qcStatus}
          onTabChange={setQcStatus}
          tableLoading={loading}
          addLabel="Add Content"
          onAdd={
            canCreateContent
              ? () =>
                  navigate("/video", {
                    state: {
                      name: ADD_STATE_BY_TYPE[selectedContentType] || "movie",
                    },
                  })
              : undefined
          }
        />
        <FloatingButton />

        {/* Rejection feedback for the item the creator just opened. */}
        <RejectionModal
          open={!!rejectionInfo}
          contentName={rejectionInfo?.name}
          reason={rejectionInfo?.reason}
          note={rejectionInfo?.note}
          onClose={() => setRejectionInfo(null)}
          onEdit={() => {
            const info = rejectionInfo;
            setRejectionInfo(null);
            if (info) openEditor(info.id, info.metadata_id);
          }}
        />
      </div>
    </>
  );
};

export default Movie;
